import type { Device } from '@studio/models/device';
import { isTransient } from '@studio/models/device';
import type { T_Maybe } from '@tryiris-ai/js-types';
import { broker } from './broker.js';
import { locationCache } from './constants.js';
import type { Proxy } from './types.js';
import { isEmbeddedShadowDeviceId } from '#~/device/embedded-shadow-id.js';

/** Scan the broker for the proxy whose state owns the device. Reads only; caches nothing. */
const searchBroker = (deviceId: Device['id']): T_Maybe<Proxy> => {
  for (const µ of broker) {
    // Embedded cameras can publish before state-sync delivers their deterministic shadow record.
    const isUnmappedEmbeddedShadow = µ.embedded && isEmbeddedShadowDeviceId(deviceId, µ.id);
    if (µ.state.control?.device[deviceId] || isUnmappedEmbeddedShadow) {
      return µ;
    }
  }

  return undefined;
};

/**
 * Locate the agent proxy that is managing the the specified device.
 *
 * @param deviceId - Device identifier.
 * @returns Managing `Proxy` or `undefined`.
 */
export const locateProxy = (deviceId: Device['id']): T_Maybe<Proxy> => {
  const agentId = locationCache.get(deviceId);

  // Do we already know which agent is managing this device?
  if (agentId) {
    const proxy = broker.acquire(agentId);

    if (proxy) {
      return proxy;
    }

    locationCache.delete(deviceId);
  }

  // We don't know where this device is, so we'll have to search for it.
  const proxy = searchBroker(deviceId);

  if (proxy) {
    locationCache.set(deviceId, proxy.id);
  }

  return proxy;
};

/**
 * Read-only `locateProxy` for reactive selectors (ENG-4898). It resolves the same owner but never
 * writes `locationCache` (no set, no delete, no LRU promotion), so a depot selector can run it
 * on every depot write and during render without side effects.
 *
 * Cost: a cache hit is one map read. A miss is one pass over the broker's proxies with one map
 * lookup each, which is what a restored tile pays per depot write until its device lands.
 *
 * @param deviceId - Device identifier.
 * @returns Managing `Proxy` or `undefined`.
 */
export const peekProxy = (deviceId: Device['id']): T_Maybe<Proxy> =>
  broker.acquire(locationCache.peek(deviceId)) ?? searchBroker(deviceId);

/**
 * Whether the Studio's only sources are Embedded cameras: at least one Embedded camera and no
 * desktop bridge with a source of its own. Embedded cameras stream over the Cloud only, so
 * Local Mode has nothing to carry in such a Studio.
 *
 * A desktop bridge's sources are its non-transient `control.device` entries, video or audio.
 * A bridge whose control state has not synced yet counts as having sources.
 * Per rule: absent-data-gates-permissively (ai.knowledge/global). Unknown never refuses Local.
 *
 * @param agents - The Studio's agent proxies.
 * @returns `true` when every source in the Studio is an Embedded camera.
 */
export const hasOnlyEmbeddedCameras = (
  agents: Iterable<Pick<Proxy, 'embedded' | 'state'>>
): boolean => {
  let hasEmbeddedCamera = false;

  for (const agent of agents) {
    if (agent.embedded) {
      hasEmbeddedCamera = true;
      continue;
    }

    const devices = agent.state.control?.device;
    if (!devices || Object.keys(devices).some((id) => !isTransient(<Device['id']>id))) {
      return false;
    }
  }

  return hasEmbeddedCamera;
};
